import { DatePipe } from '@angular/common';
import { Component, ElementRef, inject, input, OnInit, signal, viewChild, computed, model } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { IRicheditComponent } from '@devinforius/i-dx-editor';
import { AreaDropEvent, DroppableArea } from '@devinforius/i-essentials/lib/interfaces';
import { IFile, IViewerConfig } from '@devinforius/i-viewer';
import { PortaliSignComponent } from '@shared/components/portal-iSign/portal-isign.component';
import { ONE_HUNDRED_MB } from '@shared/consts';
import { BootstrapCommonIcons, GenerationRequestStatus } from '@shared/enums';
import { formWithCrossFieldsValidation, download, getFileTypeLabel } from '@shared/helpers';
import { DocumentsNFilesTarget } from '@shared/interfaces/documents-n-files-target.interface';
import { MeetingDocumentModelDto, MeetingFileDto } from '@shared/interfaces/dtos/meetings';
import { UpdateMeetingFile } from '@shared/interfaces/meetings';
import { MeetingGenerationQueue } from '@shared/models/meetings';
import { MeetingDocumentsService } from '@shared/services/meeting-documents.service';
import { MeetingFilesService } from '@shared/services/meeting-files.service';
import { PointDocumentsService } from '@shared/services/point-documents.service';
import { PointFilesService } from '@shared/services/point-files.service';
import { MyOwnRightsService } from '@shared/services/my-own-rights.service';
import { BootstrapIcon, FileExtension } from '@shared/types/dev';
import { FileUtil, FormValidators, IconUtils } from '@shared/utils';
import { DocumentFormatApi } from 'devexpress-richedit/lib/model-api/formats/enum';
import { DialogService } from 'primeng/dynamicdialog';
import { forkJoin, finalize, skip, filter, distinctUntilChanged } from 'rxjs';
import { NotificationError, NotificationSuccess, NotificationWarn } from 'src/app/core/models';
import { Notifier } from 'src/app/core/services';
import { IViewerConfigService } from 'src/app/core/services/iviewer.service';
import { Translater } from 'src/app/core/services/translater';
import { FileDocumentModelDto } from '@shared/interfaces/dtos/points/file-document-model-dto.interface';
import { Meeting } from '@shared/models/meeting';
import { SignFileComponent } from '../signFile/sign-file.component';
import { PointsService } from '@shared/services/points.service';

@Component({
  selector: 'idel-documents-n-files',
  templateUrl: './documents-n-files.component.html',
  styleUrl: './documents-n-files.component.scss',
})
export class DocumentsNFilesComponent implements OnInit {
  public readonly editor = viewChild.required<IRicheditComponent>(IRicheditComponent);
  public readonly annexeFileInput = viewChild.required<ElementRef<HTMLInputElement>>('annexeFileInput');
  public readonly associeFileInput = viewChild.required<ElementRef<HTMLInputElement>>('associeFileInput');

  private readonly meetingDocumentsService = inject(MeetingDocumentsService);
  private readonly meetingFilesService = inject(MeetingFilesService);
  private readonly pointDocumentsService = inject(PointDocumentsService);
  private readonly pointFilesService = inject(PointFilesService);
  private readonly pointsService = inject(PointsService);
  private readonly dialogRef = inject(DialogService);
  private readonly meetingGenerationQueue = inject(MeetingGenerationQueue);
  private readonly notifier = inject(Notifier);
  private readonly datePipe = inject(DatePipe);
  private readonly translater = inject(Translater);
  private readonly rights = toSignal(inject(MyOwnRightsService).fetchMyOwnRights());

  public readonly pdfViewerConfig = {
    ...inject(IViewerConfigService).initializePdfViewerConfig(),
    defaultViewMode: 'FIT_WIDTH',
  } as IViewerConfig;

  public readonly target = input.required<DocumentsNFilesTarget>();
  public readonly meeting = input<Meeting>();

  public readonly filesCounter = model.required<number>();

  public readonly documentsCollapsed = signal<boolean>(false);
  public readonly filesCollapsed = signal<boolean>(false);
  public readonly fetchingDocumentModels = signal<boolean>(false);
  public readonly fetchingFiles = signal<boolean>(false);
  public readonly uploadingFile = signal<boolean>(false);
  public readonly updatingFile = signal<boolean>(false);
  public readonly savingRtf = signal<boolean>(false);
  public readonly deletingFiles = signal<boolean>(false);
  public readonly documents = signal<
    ((MeetingDocumentModelDto | FileDocumentModelDto) & {
      generating: boolean;
      shouldGenerate: boolean;
      previewGenerating: boolean;
      generatingCompliantExtract?: boolean;
    })[]
  >([]);
  public readonly files = signal<MeetingFileDto[]>([]);
  public readonly currentDocumentToDisplay = signal<{
    document: (MeetingDocumentModelDto | FileDocumentModelDto) | null;
    mode: 'pdf' | 'editor';
  }>({ document: null, mode: 'pdf' });
  public readonly currentFileForViewer = signal<IFile | null>(null);
  public readonly currentFileToDisplay = signal<MeetingFileDto | null>(null);

  public readonly selectedFileIds = signal<Set<number>>(new Set());
  public readonly allFilesSelected = computed(() => {
    const files = this.files();
    return files.length > 0 && files.every((f) => this.selectedFileIds().has(f.id));
  });
  public readonly someFilesSelected = computed(() => this.selectedFileIds().size > 0);
  public readonly allDocumentsSelected = computed(() => {
    const docs = this.documents();
    return docs.length > 0 && docs.every((d) => d.shouldGenerate);
  });
  public readonly selectedDocumentsCount = computed(() => this.documents().filter((d) => d.shouldGenerate).length);
  public readonly droppableAreas = signal<DroppableArea[]>([
    { identifier: 1, text: 'Fichier(s) associée(s)' },
    { identifier: 2, text: 'Fichier(s) annexe(s)', restrictions: ['application/pdf'] },
  ]);
  public readonly fileBeingEdited = signal<MeetingFileDto | null>(null);
  public readonly isUpdateFileDialogOpen = signal<boolean>(false);
  public readonly isCompliantExtractGenerationDialogOpen = signal<boolean>(false);

  public readonly fileEditForm = formWithCrossFieldsValidation(
    new FormGroup({
      description: new FormControl<string>('', [Validators.required, FormValidators.notEmptyString()]),
      order: new FormControl<number | null>(null, FormValidators.positive()),
      asAttachement: new FormControl<boolean>(false),
      attachementNumber: new FormControl<number | null>(null, FormValidators.positive()),
      visibleForRepresentatives: new FormControl<boolean>(false),
      visibleForCitizens: new FormControl<boolean>(false),
    }),
    [['asAttachement', 'attachementNumber']]
  );

  public readonly icons: { [key: string]: BootstrapCommonIcons | BootstrapIcon } = {
    download: BootstrapCommonIcons.DOWNLOAD,
    see: BootstrapCommonIcons.SEE,
    preview: 'bi bi-search',
    sign: 'bi bi-pen-fill',
    generate: 'bi bi-file-earmark-pdf-fill',
    compliantExtract: 'bi bi-file-earmark-text-fill',
    update: BootstrapCommonIcons.EDIT,
  };

  public readonly singleFileMaximumSize: number = ONE_HUNDRED_MB;
  public readonly totalFilesMaximumSize: number = 3 * ONE_HUNDRED_MB;

  public readonly emptyDocumentsOptions = { retry: () => this.fetchDocuments() };
  public readonly emptyFilesOptions = { retry: () => this.fetchFiles() };

  public readonly getFileTypeLabel = getFileTypeLabel;

  private readonly ERROR_STATUSES = [
    GenerationRequestStatus.Cancelled,
    GenerationRequestStatus.Rejected,
    GenerationRequestStatus.Failed,
  ];

  public get massiveGenerationText(): string {
    const toGenerate = this.documents().filter((doc) => doc.shouldGenerate);
    if (toGenerate.length === 0) return 'Aucun document sélectionné';
    return `Générer ${toGenerate.length} document(s)`;
  }

  public get canGenerateMassively(): boolean {
    return this.documents().some((doc) => doc.shouldGenerate);
  }

  constructor() {
    toObservable(this.meetingGenerationQueue.generationIsOverFor)
      .pipe(
        skip(1),
        filter((id) => id !== -1),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.fetchDocuments();
        this.fetchFiles();
      });

    // Re-fetch when navigating between points/meetings while this tab is active
    toObservable(this.target)
      .pipe(
        distinctUntilChanged((a, b) => a.id === b.id && a.scope === b.scope),
        skip(1),
        takeUntilDestroyed()
      )
      .subscribe(() => this.reload());
  }

  ngOnInit(): void {
    this.fetchDocuments();
    this.fetchFiles();
  }

  public reload(): void {
    this.fetchDocuments();
    this.fetchFiles();
  }

  private fetchDocuments(): void {
    const { scope, id } = this.target();

    if (!this.meetingGenerationQueue.queue().length) {
      this.fetchingDocumentModels.set(true);
    }

    (scope === 'meeting'
      ? this.meetingDocumentsService.fetchMeetingDocumentModels(id)
      : this.pointDocumentsService.fetchPointDocumentModels(id)
    )
      .pipe(finalize(() => this.fetchingDocumentModels.set(false)))
      .subscribe({
        next: (documentModels) => {
          this.documents.set(
            documentModels.map((doc) => ({
              ...doc,
              generating: false,
              shouldGenerate: false,
              previewGenerating: false,
            }))
          );
          this.setLoadingStateForDocumentsStillInQueue();
        },
      });
  }

  private fetchFiles(): void {
    const { scope, id } = this.target();
    this.fetchingFiles.set(true);

    (scope === 'meeting' ? this.meetingFilesService.fetchMeetingFiles(id) : this.pointFilesService.fetchPointFiles(id))
      .pipe(finalize(() => this.fetchingFiles.set(false)))
      .subscribe({
        next: (files) => {
          this.files.set(files);
          this.filesCounter.set(files.length);
        },
      });
  }

  private setLoadingStateForDocumentsStillInQueue(): void {
    const inQueue = this.meetingGenerationQueue.queue();

    if (inQueue.length === 0) {
      this.documents.update((documents) => documents.map((doc) => ({ ...doc, generating: false })));
      return;
    }

    this.documents.update((documents) =>
      documents.map((doc) => ({
        ...doc,
        generating: this.meetingGenerationQueue.has({
          modeleId: doc.id,
          meetingId: this.target().scope === 'meeting' ? this.target().id : null,
          pointId: this.target().scope === 'point' ? this.target().id : null,
        })
          ? true
          : doc.generating,
      }))
    );
  }

  private isDocumentAlreadyGenerating(documentId: number): boolean {
    const doc = this.documents().find((d) => d.id === documentId);
    return doc ? doc.generating : false;
  }

  private resetDocumentDisplay(): void {
    this.currentDocumentToDisplay.set({ document: null, mode: 'pdf' });
    this.currentFileToDisplay.set(null);
    this.currentFileForViewer.set(null);
  }

  private canUpload(files: FileList | File[]): boolean {
    if (!files.length) return false;

    if (Array.from(files).some((file) => FileUtil.exceedsSize(file, this.singleFileMaximumSize))) {
      this.notifier.showWarning(
        new NotificationWarn({
          title: this.translater.translates('error.invalidFileSizeMessage3'),
          message: 'Fichier trop large',
        })
      );
      return false;
    }

    const sumSizes = Array.from(files).reduce((acc, file) => acc + file.size, 0);
    if (sumSizes > this.totalFilesMaximumSize) {
      this.notifier.showWarning(
        new NotificationWarn({
          title: this.translater.translates('error.invalidFileSizeMessage3'),
          message: 'La somme totale de la taille des fichiers dépasse la taille maximum autorisée',
        })
      );
      return false;
    }

    return true;
  }

  public canGenerateCompliantExtract(document: MeetingDocumentModelDto): boolean {
    if (this.target().scope === 'meeting') return false;

    return document.rights.canGenerateExtraitConf;
  }

  private uploadFile(files: File[], isAttachement: boolean): void {
    if (this.uploadingFile()) return;

    const { scope, id } = this.target();
    this.uploadingFile.set(true);

    (scope === 'meeting'
      ? this.meetingFilesService.uploadMeetingFiles(id, files, isAttachement)
      : this.pointFilesService.uploadPointFiles(id, files, isAttachement)
    )
      .pipe(finalize(() => this.uploadingFile.set(false)))
      .subscribe({
        next: () => {
          this.notifier.showSuccess(
            new NotificationSuccess({ message: this.translater.translates('success.addFile') })
          );
          this.fetchFiles();
        },
      });
  }

  public getGeneratedMessage(document: MeetingDocumentModelDto): string {
    if (document.isAlreadyGeneratedAndSaved) {
      return `Généré le ${this.datePipe.transform(document.generatedAndSavedAtDateTime, 'EEE dd/MM/yyyy à HH:mm')}`;
    } else if (document?.lastFinishedGenerationRequest) {
      return `Aperçu généré le ${this.datePipe.transform(document.lastFinishedGenerationRequest.lastRequestDateTime, 'EEE dd/MM/yyyy à HH:mm')}`;
    }
    return "Pas encore d'aperçu";
  }

  public isDocumentGenerated(document: MeetingDocumentModelDto): boolean {
    return (
      document.rights.canDownloadRtf &&
      document.isAlreadyGeneratedAndSaved &&
      document?.myLastGenerationRequest &&
      document?.myLastGenerationRequest?.statutId === GenerationRequestStatus.Finished
    );
  }

  public isDocumentPreviewGenerated(document: MeetingDocumentModelDto): boolean {
    return document.rights.canDownloadPdf;
  }

  public isDocumentCurrentlyViewed(document: MeetingDocumentModelDto): boolean {
    const current = this.currentDocumentToDisplay();
    if (!current.document || !document || !this.canSeeDocument(document)) return false;
    return current.document.id === document.id;
  }

  public isDocumentBeingGenerated(document: MeetingDocumentModelDto): boolean {
    return this.meetingGenerationQueue.has({ modeleId: document.id });
  }

  private takeoffDocumentFromUpdateOrReader(document: MeetingDocumentModelDto): void {
    const currentDisplayedDocument = this.currentDocumentToDisplay();

    if (currentDisplayedDocument?.document?.id === document.id) {
      this.currentDocumentToDisplay.set({ ...currentDisplayedDocument, document: null });
    }
  }

  public generateDocument(document: MeetingDocumentModelDto, asPreview: boolean = false): void {
    if (this.isDocumentAlreadyGenerating(document.id)) return;

    if (asPreview && !document.rights.canSubmitModelePreviewRequest) return;

    if (!asPreview && !document.rights.canGenerateModele) return;

    const documentToGenerate = this.documents().find((doc) => doc.id === document.id);
    if (!documentToGenerate) return;

    if (asPreview) {
      documentToGenerate.previewGenerating = true;
    } else {
      documentToGenerate.generating = true;
    }

    this.documents.update((documents) => [...documents]);
    this.meetingGenerationQueue.addToQueue({
      modeleId: document.id,
      meetingId: this.target().scope === 'meeting' ? this.target().id : null,
      pointId: this.target().scope === 'point' ? this.target().id : null,
    });

    this.takeoffDocumentFromUpdateOrReader(document);

    const { scope, id } = this.target();
    (scope === 'meeting'
      ? this.meetingDocumentsService.generateMeetingDocument(id, document.id, { asPreview })
      : this.pointDocumentsService.generatePointDocument(id, document.id, { asPreview })
    ).subscribe({
      next: () => {
        this.notifier.showSuccess(
          new NotificationSuccess({
            message: `Le document ${document.description} a été rajouté dans la file de génération. (La génération peut prendre un peu de temps...)`,
          })
        );
      },
      error: () => {
        this.meetingGenerationQueue.removeFromQueue({
          modeleId: document.id,
          meetingId: this.target().scope === 'meeting' ? this.target().id : null,
          pointId: this.target().scope === 'point' ? this.target().id : null,
        });
        this.documents.update((documents) =>
          documents.map((doc) =>
            doc.id === document.id ? { ...doc, generating: false, previewGenerating: false } : doc
          )
        );
        this.notifier.showError(new NotificationError({ message: 'La génération a échoué' }));
      },
    });
  }

  public generateDocumentPreview(document: MeetingDocumentModelDto): void {
    if (!document.rights.canSubmitModelePreviewRequest) return;

    const documentToGenerate = this.documents().find((doc) => doc.id === document.id);
    if (!documentToGenerate) return;

    documentToGenerate.previewGenerating = true;

    this.documents.update((documents) => [...documents]);
    this.meetingGenerationQueue.addToQueue({
      modeleId: document.id,
      meetingId: this.target().scope === 'meeting' ? this.target().id : null,
      pointId: this.target().scope === 'point' ? this.target().id : null,
    });

    const { scope, id } = this.target();
    (scope === 'meeting'
      ? this.meetingDocumentsService.generateMeetingDocumentPreview(id, document.id)
      : this.pointDocumentsService.generatePointDocumentPreview(id, document.id)
    ).subscribe({
      next: () => {
        this.notifier.showSuccess(
          new NotificationSuccess({
            message: `L'aperçu du document ${document.description} est en cours de génération. (La génération peut prendre un peu de temps...)`,
          })
        );
      },
      error: () => {
        this.meetingGenerationQueue.removeFromQueue({
          modeleId: document.id,
          meetingId: this.target().scope === 'meeting' ? this.target().id : null,
          pointId: this.target().scope === 'point' ? this.target().id : null,
        });
        this.documents.update((documents) =>
          documents.map((doc) =>
            doc.id === document.id ? { ...doc, generating: false, previewGenerating: false } : doc
          )
        );
        this.notifier.showError(new NotificationError({ message: 'La génération a échoué' }));
      },
    });
  }

  public canUpdateDocument(document: MeetingDocumentModelDto): boolean {
    return document.rights.canDownloadRtf;
  }

  public canSignDocument(document: MeetingDocumentModelDto): boolean {
    const rights = this.rights();
    if (!rights) return false;
    return (
      rights.hasAccessToISign &&
      !!document.isAlreadyGeneratedAndSaved &&
      document.rights.canSendToiSign &&
      !document.rights.iSignStatus
    );
  }

  public signDocument(document: MeetingDocumentModelDto): void {
    if (!this.canSignDocument(document)) return;

    const { scope, id } = this.target();
    const ref = this.dialogRef.open(PortaliSignComponent, {
      data: {
        modeleSelected: [document],
        ...(scope === 'meeting' ? { meeting: [{ id }] } : { point: [{ id }] }),
        showHeader: true,
        header: 'Signature iSign',
        width: '50vw',
        baseZIndex: 1000,
      },
    });
    ref.onClose.subscribe((res: string) => {
      if (res === 'generate') this.fetchDocuments();
    });
  }

  public updateDocument(document: MeetingDocumentModelDto): void {
    if (!this.canUpdateDocument(document) || this.isDocumentBeingGenerated(document)) return;

    const { scope, id } = this.target();
    this.currentFileToDisplay.set(null);
    this.currentDocumentToDisplay.set({ document, mode: 'editor' });

    (scope === 'meeting'
      ? this.meetingDocumentsService.downloadMeetingDocumentRTF(id, document.id)
      : this.pointDocumentsService.downloadPointDocumentRTF(id, document.id)
    ).subscribe({
      next: (blob) => {
        this.editor().rich?.openDocument(blob, (blob as File).name, DocumentFormatApi.Rtf);
      },
      error: () => this.notifier.showError(new NotificationError({ message: 'Erreur lors de la récupération du RTF' })),
    });
  }

  public canDownloadDocument(document: MeetingDocumentModelDto): boolean {
    return document.rights.canDownloadPdf;
  }

  public canSeeDocument(document: MeetingDocumentModelDto): boolean {
    return document.rights.canDownloadPdf;
  }

  public downloadDocument(document: MeetingDocumentModelDto): void {
    if (!this.canDownloadDocument(document)) return;

    const { scope, id } = this.target();
    (scope === 'meeting'
      ? this.meetingDocumentsService.downloadMeetingDocumentPDF(id, document.id)
      : this.pointDocumentsService.downloadPointDocumentPDF(id, document.id)
    ).subscribe({
      next: (blob) => download(URL.createObjectURL(blob), `${document.description}.pdf`),
      error: () => this.notifier.showError(new NotificationError({ message: 'Erreur lors de la récupération du PDF' })),
    });
  }

  public seeDocument(document: MeetingDocumentModelDto): void {
    if (!this.canSeeDocument(document)) return;

    const { scope, id } = this.target();
    this.currentFileToDisplay.set(null);
    this.currentDocumentToDisplay.set({ document, mode: 'pdf' });

    (scope === 'meeting'
      ? this.meetingDocumentsService.downloadMeetingDocumentPDF(id, document.id)
      : this.pointDocumentsService.downloadPointDocumentPDF(id, document.id)
    ).subscribe({
      next: (blob) => {
        this.currentFileForViewer.set({
          src: URL.createObjectURL(blob),
          extension: 'pdf',
          title: document.description,
        });
      },
    });
  }

  public toggleFileSelection(file: MeetingFileDto): void {
    this.selectedFileIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(file.id)) next.delete(file.id);
      else next.add(file.id);
      return next;
    });
  }

  public toggleAllFiles(): void {
    if (this.allFilesSelected()) {
      this.selectedFileIds.set(new Set());
    } else {
      this.selectedFileIds.set(new Set(this.files().map((f) => f.id)));
    }
  }

  public toggleAllDocuments(): void {
    const selectAll = !this.allDocumentsSelected();
    this.documents.update((docs) => docs.map((d) => ({ ...d, shouldGenerate: selectAll })));
  }

  public setFileVisibility(config: {
    file: MeetingFileDto;
    field: 'visibleDelibeWeb' | 'visibleDelibeWebCitoyen';
    visible: boolean;
  }): void {
    const { scope, id } = this.target();
    const body = {
      visibleForRepresentatives: config.field === 'visibleDelibeWeb' ? config.visible : config.file.visibleDelibeWeb,
      visibleForCitizens:
        config.field === 'visibleDelibeWebCitoyen' ? config.visible : config.file.visibleDelibeWebCitoyen,
    };

    (scope === 'meeting'
      ? this.meetingFilesService.setMeetingFilesVisibility(id, [config.file.id], body)
      : this.pointFilesService.setPointFilesVisibility(id, [config.file.id], body)
    ).subscribe({
      next: () => {
        this.notifier.showSuccess(
          new NotificationSuccess({ message: `Visibilité changée pour le fichier ${config.file.description}.` })
        );
        this.fetchFiles();
        this.selectedFileIds.set(new Set());
      },
    });
  }

  public setFilesVisibility(field: 'visibleDelibeWeb' | 'visibleDelibeWebCitoyen', visible: boolean): void {
    const ids = [...this.selectedFileIds()];
    if (!ids.length) return;

    const { scope, id } = this.target();
    const body: { visibleForRepresentatives?: boolean; visibleForCitizens?: boolean } = {};
    if (field === 'visibleDelibeWeb') {
      body.visibleForRepresentatives = visible;
    } else {
      body.visibleForCitizens = visible;
    }

    (scope === 'meeting'
      ? this.meetingFilesService.setMeetingFilesVisibility(id, ids, body)
      : this.pointFilesService.setPointFilesVisibility(id, ids, body)
    ).subscribe({
      next: () => {
        this.notifier.showSuccess(new NotificationSuccess({ message: 'Visibilité pour les fichiers changée.' }));
        this.fetchFiles();
        this.selectedFileIds.set(new Set());
      },
    });
  }

  public deleteSelectedFiles(): void {
    const fileIds = [...this.selectedFileIds()];

    if (!fileIds.length || this.deletingFiles()) return;

    this.deletingFiles.set(true);

    const { scope, id } = this.target();
    const currentFile = this.currentFileToDisplay();

    const deleteFn = (fileId: number) =>
      scope === 'meeting'
        ? this.meetingFilesService.deleteMeetingFile(id, fileId)
        : this.pointFilesService.deletePointFile(id, fileId);

    forkJoin(fileIds.map(deleteFn))
      .pipe(finalize(() => this.deletingFiles.set(false)))
      .subscribe({
        next: () => {
          this.notifier.showSuccess(new NotificationSuccess({ message: 'Fichier(s) supprimé(s) !' }));
          if (currentFile && fileIds.includes(currentFile.id)) this.resetDocumentDisplay();
          this.fetchFiles();
          this.selectedFileIds.set(new Set());
        },
      });
  }

  public isFileCurrentlyViewed(file: MeetingFileDto): boolean {
    const current = this.currentFileToDisplay();
    return !!current && current.id === file.id;
  }

  public seeFile(file: MeetingFileDto): void {
    const { scope, id } = this.target();
    this.currentDocumentToDisplay.set({ document: null, mode: 'pdf' });
    this.currentFileToDisplay.set(file);

    (scope === 'meeting'
      ? this.meetingFilesService.downloadMeetingFile(id, file.id)
      : this.pointFilesService.downloadPointFile(id, file.id)
    ).subscribe({
      next: (blob) => {
        this.currentFileForViewer.set({ src: URL.createObjectURL(blob), extension: 'pdf', title: file.description });
      },
    });
  }

  public downloadFile(file: MeetingFileDto): void {
    const { scope, id } = this.target();

    (scope === 'meeting'
      ? this.meetingFilesService.downloadMeetingFile(id, file.id)
      : this.pointFilesService.downloadPointFile(id, file.id)
    ).subscribe({
      next: (blob) => {
        const ext = `.${file.extension}`;
        const baseName = file.description.toLowerCase().endsWith(ext.toLowerCase())
          ? file.description.slice(0, -ext.length)
          : file.description;
        download(URL.createObjectURL(blob), `${baseName}${ext}`);
      },
    });
  }

  public uploadUpdatedDocument(): void {
    if (this.savingRtf()) return;

    const currentDocumentToDisplay = this.currentDocumentToDisplay();
    if (!currentDocumentToDisplay?.document) return;

    const { document } = currentDocumentToDisplay;
    const { scope, id } = this.target();
    this.savingRtf.set(true);

    this.editor().rich?.exportToFile((file: File) => {
      (scope === 'meeting'
        ? this.meetingDocumentsService.uploadMeetingDocumentRTF({ meetingId: id, documentModelId: document.id, file })
        : this.pointDocumentsService.uploadPointDocumentRTF({ pointId: id, documentModelId: document.id, file })
      )
        .pipe(finalize(() => this.savingRtf.set(false)))
        .subscribe({
          next: () => {
            this.notifier.showSuccess(new NotificationSuccess({ message: 'Document modifié sauvé !' }));
            this.resetDocumentDisplay();
            this.fetchDocuments();
          },
        });
    }, DocumentFormatApi.Rtf);
  }

  public getDocumentNbrOfVersions(document: MeetingDocumentModelDto): string {
    return `Version ${document.version}`;
  }

  public toggleDocuments(): void {
    const willBeCollapsed = !this.documentsCollapsed();
    if (willBeCollapsed && this.filesCollapsed()) return;
    this.documentsCollapsed.set(willBeCollapsed);
  }

  public toggleFiles(): void {
    const willBeCollapsed = !this.filesCollapsed();
    if (willBeCollapsed && this.documentsCollapsed()) return;
    this.filesCollapsed.set(willBeCollapsed);
  }

  public hasGenerationError(document: MeetingDocumentModelDto): boolean {
    const statutId = document.myLastGenerationRequest?.statutId;
    return statutId != null && this.ERROR_STATUSES.includes(statutId);
  }

  public hasGenerationErrorDetail(document: MeetingDocumentModelDto): boolean {
    return !!document?.myLastGenerationRequest?.jsonData;
  }

  public getGenerationErrorLabel(document: MeetingDocumentModelDto): string {
    switch (document.myLastGenerationRequest?.statutId) {
      case GenerationRequestStatus.Cancelled:
        return 'La génération a été annulée';
      case GenerationRequestStatus.Rejected:
        return 'La génération a été rejetée';
      case GenerationRequestStatus.Failed:
        return 'La génération a échoué';
      default:
        return '';
    }
  }

  public getFileUploadDate(file: MeetingFileDto): string {
    return this.datePipe.transform(file.dateUpload, 'EEE dd/MM/yyyy à HH:mm') as string;
  }

  private populateEditFormGroup(file: MeetingFileDto): void {
    this.fileEditForm.setValue({
      description: file.description,
      order: file.ordreAffichage,
      asAttachement: file.annexe,
      attachementNumber: file.annexeNumero,
      visibleForRepresentatives: file.visibleDelibeWeb,
      visibleForCitizens: file.visibleDelibeWebCitoyen,
    });
  }

  public openEditDialog(file: MeetingFileDto): void {
    this.fileBeingEdited.set(file);
    this.populateEditFormGroup(file);
    this.isUpdateFileDialogOpen.set(true);
  }

  public closeEditDialog(): void {
    this.isUpdateFileDialogOpen.set(false);
    this.fileBeingEdited.set(null);
    this.fileEditForm.reset();
  }

  public confirmEditFile(): void {
    const file = this.fileBeingEdited();
    if (!file || !this.fileEditForm || this.updatingFile()) return;

    const { value } = this.fileEditForm;
    const body: UpdateMeetingFile = {
      Id: file.id,
      Annexe: value.asAttachement ?? file.annexe,
      AnnexeNumero: value.attachementNumber ?? file.annexeNumero,
      Description: value.description!,
      OrdreAffichage: value.order ?? null,
      VisibleDelibeWeb: value.visibleForRepresentatives ?? file.visibleDelibeWeb,
      VisibleDelibeWebCitoyen: value.visibleForCitizens ?? file.visibleDelibeWebCitoyen,
    };

    const { scope, id } = this.target();
    (scope === 'meeting'
      ? this.meetingFilesService.updateMeetingFile(id, file.id, body)
      : this.pointFilesService.updatePointFile(id, file.id, body)
    )
      .pipe(finalize(() => this.updatingFile.set(false)))
      .subscribe({
        next: () => {
          this.notifier.showSuccess(new NotificationSuccess({ message: 'Fichier modifié avec succès.' }));
          this.closeEditDialog();
          this.fetchFiles();
        },
      });
  }

  public canPreviewFile(file: MeetingFileDto): boolean {
    return file.extension.toLowerCase() === 'pdf';
  }

  public getFileIcon(extension: string): string {
    const ext = ('.' + extension.toLowerCase()) as FileExtension;
    return IconUtils.getIconByExtension(ext) ?? 'bi bi-file';
  }

  public getFileIconColorClass(extension: string): string {
    const ext = extension.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'tiff', 'bmp', 'svg', 'webp'].includes(ext)) return 'file-icon--image';
    if (ext === 'pdf') return 'file-icon--pdf';
    if (['doc', 'docx', 'odt'].includes(ext)) return 'file-icon--word';
    if (['xls', 'xlsx', 'ods'].includes(ext)) return 'file-icon--excel';
    if (['ppt', 'pptx', 'odp'].includes(ext)) return 'file-icon--powerpoint';
    if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'weba'].includes(ext)) return 'file-icon--audio';
    if (['mp4', 'webm', 'ogv', 'mov', 'avi', 'wmv'].includes(ext)) return 'file-icon--video';
    if (['zip', 'tar', 'gz', 'rar', '7z'].includes(ext)) return 'file-icon--archive';
    return 'file-icon--default';
  }

  public triggerFileInput(type: 'annexe' | 'associe'): void {
    const input = type === 'annexe' ? this.annexeFileInput() : this.associeFileInput();
    input.nativeElement.click();
  }

  public onFileInputChange(event: Event, isAttachement: boolean): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files || !this.canUpload(files)) return;
    this.uploadFile(Array.from(files), isAttachement);
    input.value = '';
  }

  public onFileDropped(dropEvent: AreaDropEvent): void {
    const { files, areaIdentifier } = dropEvent;
    if (!this.canUpload(files)) return;
    this.uploadFile(Array.from(files), areaIdentifier !== 1);
  }

  public generateCompliantExtract(document: FileDocumentModelDto): void {
    const meeting = this.meeting();
    const target = this.target();

    if (!meeting || target.scope !== 'point' || !this.canGenerateCompliantExtract(document)) return;

    this.pointsService.fetchOnePoint(this.target().id).subscribe({
      next: (point) => {
        this.dialogRef
          .open(SignFileComponent, {
            data: {
              seanceId: meeting.id,
              massGeneration: document.rights.canGenerateExtraitConf,
              points: [point],
              fromPoint: true,
              modelSelected: document.id,
              allowModelSelection: false,
            },
            showHeader: true,
            header: "Générer l'extrait conforme pour ce point",
            width: '50vw',
            baseZIndex: 1000,
          })
          .onClose.subscribe((generated: boolean) => {
            this.isCompliantExtractGenerationDialogOpen.set(false);
            if (generated) {
              this.fetchDocuments();
              this.fetchFiles();
            }
          });
      },
    });
  }

  public closeGenerationDialog(): void {
    this.isCompliantExtractGenerationDialogOpen.set(false);
  }
}
